import { Trans, useTranslation } from "react-i18next";
import OrderContent from "../Checkout/OrderContent";
import React, { useEffect, useMemo, useState } from "react";
import { useAPI } from "../../../lib/common";
import makeStyles from "@mui/styles/makeStyles";
import { usePortal } from "../../../Providers/PortalProvider";
import { PRODUCT_RETOUCH_FILE_UUID, usePriceList, } from "../../providers/PriceListProvider";
import Address from "../Address";
import useRoutes, { getLinkComponentAttributes } from "../../hooks/useRoutes";
import { PageHeader } from "../MobilePage";
import { isServerProductOrderLine } from "@/react/Webshop/types/ShoppingCart";
import AddAnotherSubjectCodeBox from "../../SimpleWebshop/components/AddAnotherSubjectCodeBox";
import Button from "@/react/components/Common/Button";
import { Card, CardContent, Link, Box, Dialog, DialogActions, DialogContent, Typography, Button as MUIButton, } from "@mui/material";
import { markPurchaseTracked, shouldTrackPurchase, } from "../../utils/purchaseTracking";
import { sendWhenEpmsAnalyticsAllows } from "../../utils/epmsAnalyticsReady";
const useStyles = makeStyles((theme) => ({
    container: {
        background: "linear-gradient(135deg, transparent 5.68px, white 5.69px) top left, linear-gradient(45deg, white 2.8px, transparent 2.81px) top left, linear-gradient(135deg, white 2.8px, transparent 2.81px) bottom left, linear-gradient(45deg, transparent 5.68px, white 5.69px) bottom left",
        backgroundRepeat: "repeat-x",
        backgroundSize: "8px 4px",
        boxShadow: "0 0 0 rgba(0, 0, 0, 0.1)",
        padding: "4px 0",
        marginBottom: theme.spacing(4),
    },
    cardContent: {
        backgroundColor: "white",
    },
    deliveryInfo: {
        textAlign: "center",
    },
    orderReferenceDescription: {
        fontSize: "16px",
        fontWeight: "normal",
        textAlign: "center",
        marginBottom: 0,
    },
    orderReference: {
        fontSize: "20px",
        fontWeight: "bold",
        textAlign: "center",
        marginTop: 0,
    },
    receiptSpacer: {
        marginTop: theme.spacing(3),
        marginBottom: theme.spacing(3),
        borderBottom: "2px dashed #e3e9eb",
    },
}));
const getContactText = (t, portal) => {
    return {
        __html: t("If you have any questions, please don't hesitate to %scontact us.%s", {
            postProcess: "sprintf",
            sprintf: [
                "<a href='mailto:" +
                    portal.contactInfo.email +
                    "' class='MuiTypography-root MuiLink-root MuiLink-underlineHover MuiTypography-colorPrimary'>",
                "</a>",
            ],
        }),
    };
};
const AddPictureCodeDialog = ({ isOpen, onClose, }) => {
    return (React.createElement(Dialog, { open: isOpen, onClose: onClose, fullWidth: true, maxWidth: "sm" },
        React.createElement(DialogContent, null,
            React.createElement(Typography, { variant: "h6", component: "h2", sx: { mb: 2 } },
                React.createElement(Trans, null, "Add another picture code")),
            React.createElement(Box, null,
                React.createElement(AddAnotherSubjectCodeBox, { alertOnSameSubject: false, showTitle: false }))),
        React.createElement(DialogActions, null,
            React.createElement(MUIButton, { onClick: onClose, color: "primary" },
                React.createElement(Trans, null, "Close")))));
};
const OrderReceipt = ({ orderUuid, digitalDeliveryText, }) => {
    const classes = useStyles();
    const [order, loading] = useAPI("/api/webshop/user/orders/" + orderUuid);
    const { t } = useTranslation();
    const portal = usePortal();
    const [showAddCodeInputDialog, setShowAddCodeInputDialog] = useState(false);
    useEffect(() => {
        // The receipt is revisitable, so a reload would report the sale twice.
        if (!order || !shouldTrackPurchase(order)) {
            return;
        }
        return sendWhenEpmsAnalyticsAllows((analytics) => {
            const delivered = analytics.onPurchaseSuccessful(order);
            if (delivered) {
                markPurchaseTracked(order.uuid);
            }
            return delivered;
        });
    }, [order]);
    const numRetouchFiles = useMemo(() => {
        if (!order) {
            return 0;
        }
        return order.orderLines.filter((ol) => isServerProductOrderLine(ol) &&
            ol.product.uuid === PRODUCT_RETOUCH_FILE_UUID).length;
    }, [order]);
    if (!order || loading) {
        return React.createElement("div", null, "Loading");
    }
    return (React.createElement(React.Fragment, null,
        React.createElement(Card, { raised: false, className: classes.container, elevation: 0 },
            React.createElement(CardContent, { className: classes.cardContent },
                React.createElement(PageHeader, null,
                    React.createElement(Trans, null, "Order receipt")),
                React.createElement("div", { className: classes.deliveryInfo },
                    React.createElement("h4", { className: classes.orderReferenceDescription },
                        React.createElement(Trans, null, "Order reference")),
                    React.createElement("h5", { className: classes.orderReference }, orderUuid.substring(0, 8)),
                    React.createElement(PaymentDetails, { order: order }),
                    React.createElement(DeliveryDetails, { order: order }),
                    numRetouchFiles > 0 && (React.createElement("p", null,
                        React.createElement(Trans, { i18nKey: "You have ordered image retouch for <1>{{numPictures}} images</1>. Our retouch team will work carefully to meet your expectations, and you will receive an email notification when your images are ready.", values: { numPictures: numRetouchFiles }, components: [React.createElement("b", { key: "1" })] }))),
                    order.catalogueMaxDeliveryTime && (React.createElement(CatalogueDeliveryText, { deliveryDays: order.catalogueMaxDeliveryTime })),
                    order.containDigitalProducts &&
                        order.delivery.type != "digital-delivery" && (React.createElement(React.Fragment, null, digitalDeliveryText !== null && digitalDeliveryText !== void 0 ? digitalDeliveryText : React.createElement(DigitalDeliveryText, null))),
                    React.createElement("p", null,
                        React.createElement(Trans, null, "An order receipt has been sent to you by e-mail.")),
                    order.delivery.type == "digital-delivery" && (React.createElement("p", null,
                        React.createElement(Trans, null, "In addition to the order receipt you will instantly receive an email containing a download link. Purchased digital portaits are also available at all times at 'My page - Digital Downloads'."))),
                    React.createElement("p", { dangerouslySetInnerHTML: getContactText(t, portal) }),
                    React.createElement("p", null,
                        React.createElement(Trans, null, "Kind regards,"),
                        React.createElement("br", null),
                        portal.name)),
                React.createElement(ReceiptSpacer, null),
                React.createElement(Button, { variant: "primary", type: "button", size: "large", fullWidth: true, onClick: () => setShowAddCodeInputDialog(true) },
                    React.createElement(Trans, null, "Add another picture code?")),
                React.createElement(ReceiptSpacer, null),
                React.createElement(OrderContent, { orderLines: order.orderLines, rebateCode: order.rebateCode, hasActiveRebateCampaigns: false }))),
        React.createElement(AddPictureCodeDialog, { isOpen: showAddCodeInputDialog, onClose: () => setShowAddCodeInputDialog(false) })));
};
const PaymentDetails = ({ order }) => {
    const { t } = useTranslation();
    const priceList = usePriceList();
    if (!priceList) {
        return null;
    }
    const { state } = priceList;
    if (order.summary.totalPrice == 0) {
        return null;
    }
    if (order.payment.isInvoice) {
        return (React.createElement("p", null, t("An invoice will be sent to you with an amount of %s %0.2f.", {
            postProcess: "sprintf",
            sprintf: [state.currencyCode, order.summary.totalPrice],
        })));
    }
    return null;
};
const DeliveryDetails = ({ order }) => {
    switch (order.delivery.type) {
        case "postage":
            return React.createElement(DeliveryDetailsPostage, { delivery: order.delivery });
        case "school-pickup":
            return React.createElement(DeliveryDetailsSchool, { delivery: order.delivery });
        case "store-pickup":
            return React.createElement(DeliveryDetailsStorePickup, { delivery: order.delivery });
        case "digital-delivery":
            return null;
    }
    return null;
};
const DeliveryDetailsStorePickup = ({ delivery, }) => {
    const { t } = useTranslation();
    const getDeliveryText = () => {
        return {
            __html: t("You have chosen to pick up the order in our store. The normal delivery time is %s, but please remember to take our opening hours into account. %s Our street address is:", {
                postProcess: "sprintf",
                sprintf: [
                    "<b>" + delivery.deliveryTime.string + "</b>",
                    "<br /><br />",
                ],
            }),
        };
    };
    return (React.createElement(React.Fragment, null,
        React.createElement("p", { dangerouslySetInnerHTML: getDeliveryText() }),
        React.createElement(Address, { address: delivery.address })));
};
const DeliveryDetailsSchool = ({ delivery }) => {
    const { t } = useTranslation();
    return (React.createElement(React.Fragment, null,
        React.createElement("p", null, t("Your order will be delivered at %s after %s.", {
            postProcess: "sprintf",
            sprintf: [delivery.school.name, delivery.deliveryTime.afterDate],
        }))));
};
const DeliveryDetailsPostage = ({ delivery }) => {
    const { t } = useTranslation();
    const { address } = delivery;
    return (React.createElement(React.Fragment, null,
        React.createElement("p", null, t("You have chosen to have the order delivered to you by postal service. %s Normal delivery time is within %s and the order will be sent to:", {
            postProcess: "sprintf",
            sprintf: ["", delivery.deliveryTime.string],
        })),
        React.createElement(Address, { address: address })));
};
const ReceiptSpacer = () => {
    const classes = useStyles();
    return React.createElement("div", { className: classes.receiptSpacer });
};
const DigitalDeliveryText = () => {
    const routes = useRoutes();
    const myAccountAttributes = getLinkComponentAttributes("myaccount", routes);
    return (React.createElement("p", null,
        React.createElement(Trans, null,
            "If you have purchased digital files you will receive a separate email containing a link to where you may download the images. The images will also be available for download on",
            " ",
            React.createElement(Link, Object.assign({}, myAccountAttributes), "My account"),
            " in the webshop.")));
};
const CatalogueDeliveryText = ({ deliveryDays, }) => {
    return (React.createElement("p", null,
        React.createElement(Trans, { i18nKey: "Your order contains catalogues which are shipped separately, normally within <1>{{days}} days</1> after the last photo day.", values: { days: deliveryDays }, components: [React.createElement("b", { key: "1" })] })));
};
export default OrderReceipt;
